import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import { Agencia, CreateAgenciaDTO } from '../types/agencia';
import { agenciaService } from '../services/agenciaService';
import MainLayout from '../layouts/MainLayout';
import { useForm, Controller } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const GerenciarAgencias: React.FC = () => {
  const [agencias, setAgencias] = useState<Agencia[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [agenciaAtual, setAgenciaAtual] = useState<Agencia | null>(null);

  const {
    register,
    handleSubmit: handleSubmitForm,
    control,
    reset,
    formState: { errors },
  } = useForm<CreateAgenciaDTO>({
    defaultValues: {
      codigo: '',
      nome: '',
      nomeGerente: '',
      tipoPessoa: 'PF',
      ativo: true,
    },
  });

  const carregarAgencias = async () => {
    try {
      const data = await agenciaService.listarAgencias();
      setAgencias(data);
    } catch (error) {
      console.error('Erro ao carregar agências:', error);
    }
  };

  useEffect(() => {
    carregarAgencias();
  }, []);

  const handleOpenDialog = (agencia?: Agencia) => {
    if (agencia) {
      setAgenciaAtual(agencia);
      reset({
        codigo: agencia.codigo,
        nome: agencia.nome,
        nomeGerente: agencia.nomeGerente,
        tipoPessoa: agencia.tipoPessoa,
        ativo: agencia.ativo,
      });
    } else {
      setAgenciaAtual(null);
      reset({
        codigo: '',
        nome: '',
        nomeGerente: '',
        tipoPessoa: 'PF',
        ativo: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setAgenciaAtual(null);
    reset({
      codigo: '',
      nome: '',
      nomeGerente: '',
      tipoPessoa: 'PF',
      ativo: true,
    });
  };

  const onSubmit = async (formData: CreateAgenciaDTO) => {
    try {
      if (agenciaAtual) {
        await agenciaService.atualizarAgencia(agenciaAtual.id, formData);
        toast.success('Agência atualizada com sucesso!');
      } else {
        await agenciaService.criarAgencia(formData);
        toast.success('Agência criada com sucesso!');
      }
      handleCloseDialog();
      carregarAgencias();
    } catch (error) {
      console.error('Erro ao salvar agência:', error);
      toast.error('Erro ao salvar agência.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta agência?')) {
      try {
        await agenciaService.excluirAgencia(id);
        toast.success('Agência excluída com sucesso!');
        carregarAgencias();
      } catch (error) {
        console.error('Erro ao excluir agência:', error);
      }
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" color="primary.main" sx={{ fontWeight: 'bold' }}>
            <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2em' }} />
            Gerenciamento de Agências
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Nova Agência
          </Button>
        </Box>

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', textTransform: 'uppercase', bgcolor: 'grey.100' } }}>
                  <TableCell>Código</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Gerente</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agencias.map((agencia) => (
                  <TableRow key={agencia.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>{agencia.codigo}</TableCell>
                    <TableCell>{agencia.nome}</TableCell>
                    <TableCell>{agencia.nomeGerente}</TableCell>
                    <TableCell>{agencia.tipoPessoa === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}</TableCell>
                    <TableCell>
                      <Chip label={agencia.ativo ? 'Ativo' : 'Inativo'} color={agencia.ativo ? 'success' : 'error'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Editar">
                          <IconButton color="primary" onClick={() => handleOpenDialog(agencia)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton color="error" onClick={() => handleDelete(agencia.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth component="form" onSubmit={handleSubmitForm(onSubmit)}>
        <DialogTitle>
          {agenciaAtual ? 'Editar Agência' : 'Nova Agência'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>Dados da Agência</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Código"
              fullWidth
              {...register('codigo', { required: 'Código é obrigatório' })}
              error={!!errors.codigo}
              helperText={errors.codigo?.message}
            />
            <TextField
              label="Nome"
              fullWidth
              {...register('nome', { required: 'Nome é obrigatório' })}
              error={!!errors.nome}
              helperText={errors.nome?.message}
            />
            <TextField
              label="Nome do Gerente"
              fullWidth
              {...register('nomeGerente', { required: 'Nome do gerente é obrigatório' })}
              error={!!errors.nomeGerente}
              helperText={errors.nomeGerente?.message}
            />
            <Controller
              name="tipoPessoa"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Tipo de Pessoa" fullWidth>
                  <MenuItem value="PF">Pessoa Física</MenuItem>
                  <MenuItem value="PJ">Pessoa Jurídica</MenuItem>
                </TextField>
              )}
            />
            <Controller
              name="ativo"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label="Ativo"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button type="submit" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default GerenciarAgencias; 